/**
 * PdfSplitModal - Modal for splitting PDF documents into multiple files
 *
 * Allows users to:
 * - View thumbnails of all PDF pages
 * - Click pages to assign them to different documents (color-coded)
 * - Set filenames for each split document
 * - Optionally delete the original file after splitting
 */

import { useState, useEffect, useMemo, useCallback } from "react";
import { Document, Page, pdfjs } from "react-pdf";
import { useSession } from "@clerk/clerk-react";
import { fetchWithAuth } from "shared/api/fetch-with-auth";
import { API_URL } from "constants/index";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { Scissors, Plus, Trash, SpinnerGap, Warning, Check } from "@phosphor-icons/react";
import { toast } from "react-toastify";
import "react-pdf/dist/esm/Page/AnnotationLayer.css";
import "react-pdf/dist/esm/Page/TextLayer.css";

// Configure PDF.js worker - use CDN with version from react-pdf to avoid version mismatch
pdfjs.GlobalWorkerOptions.workerSrc = `//unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;

// Color palette for document groups
const DOCUMENT_COLORS = [
  { bg: "bg-emerald-100", border: "border-emerald-500", text: "text-emerald-700", dot: "bg-emerald-500" },
  { bg: "bg-blue-100", border: "border-blue-500", text: "text-blue-700", dot: "bg-blue-500" },
  { bg: "bg-amber-100", border: "border-amber-500", text: "text-amber-700", dot: "bg-amber-500" },
  { bg: "bg-purple-100", border: "border-purple-500", text: "text-purple-700", dot: "bg-purple-500" },
  { bg: "bg-rose-100", border: "border-rose-500", text: "text-rose-700", dot: "bg-rose-500" },
  { bg: "bg-cyan-100", border: "border-cyan-500", text: "text-cyan-700", dot: "bg-cyan-500" },
];

export interface SplitResult {
  splits: Array<{
    fileName: string;
    fileId: string;
    webUrl: string;
    pages: number[];
  }>;
  originalDeleted: boolean;
  originalFileId: string;
  folderPath: string;
}

interface PdfSplitModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSplitComplete: (result: SplitResult) => void;
  buildingId: string;
  file: {
    id: string;
    name: string;
    path: string;
  };
  folderId: string;
}

interface DocumentGroup {
  id: number;
  fileName: string;
  pages: number[]; // 1-indexed page numbers
}

export const PdfSplitModal = ({
  isOpen,
  onClose,
  onSplitComplete,
  buildingId,
  file,
  folderId,
}: PdfSplitModalProps) => {
  const { session } = useSession();
  const [pdfBlob, setPdfBlob] = useState<Blob | null>(null);
  const [numPages, setNumPages] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [isSplitting, setIsSplitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Page assignments: pageNumber -> documentGroupId (1-indexed)
  const [pageAssignments, setPageAssignments] = useState<Record<number, number>>({});
  const [documentGroups, setDocumentGroups] = useState<DocumentGroup[]>([]);
  const [deleteOriginal, setDeleteOriginal] = useState(false);

  // Derive base filename without extension
  const baseFileName = useMemo(() => {
    return file.name.replace(/\.pdf$/i, "");
  }, [file.name]);

  // Initialize document groups with 2 groups
  useEffect(() => {
    if (isOpen && documentGroups.length === 0) {
      setDocumentGroups([
        { id: 1, fileName: `${baseFileName}_deel1.pdf`, pages: [] },
        { id: 2, fileName: `${baseFileName}_deel2.pdf`, pages: [] },
      ]);
      setPageAssignments({});
      setDeleteOriginal(false);
    }
  }, [isOpen, baseFileName, documentGroups.length]);

  // Fetch PDF when modal opens
  useEffect(() => {
    if (!isOpen || !session) return;

    let isMounted = true;
    setIsLoading(true);
    setError(null);

    const fetchPdf = async () => {
      try {
        const response = await fetchWithAuth(
          `${API_URL}/graph/file/${file.id}/content`,
          { method: "GET" },
          session
        );

        if (!response.ok) {
          throw new Error("Kon PDF niet ophalen");
        }

        const blob = await response.blob();
        // Create new blob with explicit PDF MIME type for react-pdf
        const pdfBlob = new Blob([blob], { type: 'application/pdf' });
        if (isMounted) {
          setPdfBlob(pdfBlob);
          setIsLoading(false);
        }
      } catch (err) {
        console.error("Error loading PDF:", err);
        if (isMounted) {
          setError("PDF kon niet worden geladen");
          setIsLoading(false);
        }
      }
    };

    fetchPdf();

    return () => {
      isMounted = false;
    };
  }, [isOpen, file.id, session]);

  // Handle page click - cycle through document groups
  const handlePageClick = useCallback(
    (pageNumber: number) => {
      setPageAssignments((prev) => {
        const currentGroup = prev[pageNumber];
        const groupIds = documentGroups.map((g) => g.id);

        if (currentGroup === undefined) {
          // Assign to first group
          return { ...prev, [pageNumber]: groupIds[0] };
        }

        const currentIndex = groupIds.indexOf(currentGroup);
        const nextIndex = (currentIndex + 1) % (groupIds.length + 1);

        if (nextIndex === groupIds.length) {
          // Unassign
          const { [pageNumber]: _, ...rest } = prev;
          return rest;
        }

        return { ...prev, [pageNumber]: groupIds[nextIndex] };
      });
    },
    [documentGroups]
  );

  // Update document groups with their assigned pages
  useEffect(() => {
    setDocumentGroups((prev) =>
      prev.map((group) => ({
        ...group,
        pages: Object.entries(pageAssignments)
          .filter(([_, groupId]) => groupId === group.id)
          .map(([page]) => parseInt(page, 10))
          .sort((a, b) => a - b),
      }))
    );
  }, [pageAssignments]);

  // Add new document group
  const addDocumentGroup = useCallback(() => {
    if (documentGroups.length >= DOCUMENT_COLORS.length) {
      toast.warning(`Maximaal ${DOCUMENT_COLORS.length} documenten toegestaan`);
      return;
    }

    const newId = Math.max(...documentGroups.map((g) => g.id), 0) + 1;
    setDocumentGroups((prev) => [
      ...prev,
      {
        id: newId,
        fileName: `${baseFileName}_deel${newId}.pdf`,
        pages: [],
      },
    ]);
  }, [documentGroups, baseFileName]);

  // Remove document group
  const removeDocumentGroup = useCallback(
    (groupId: number) => {
      if (documentGroups.length <= 2) {
        toast.warning("Minimaal 2 documenten vereist voor splitsen");
        return;
      }

      // Remove assignments for this group
      setPageAssignments((prev) => {
        const newAssignments = { ...prev };
        Object.keys(newAssignments).forEach((page) => {
          if (newAssignments[parseInt(page, 10)] === groupId) {
            delete newAssignments[parseInt(page, 10)];
          }
        });
        return newAssignments;
      });

      setDocumentGroups((prev) => prev.filter((g) => g.id !== groupId));
    },
    [documentGroups]
  );

  // Update filename for a group
  const updateFileName = useCallback((groupId: number, fileName: string) => {
    setDocumentGroups((prev) =>
      prev.map((g) =>
        g.id === groupId ? { ...g, fileName: fileName.endsWith(".pdf") ? fileName : `${fileName}.pdf` } : g
      )
    );
  }, []);

  // Get color for a document group
  const getColorForGroup = useCallback(
    (groupId: number) => {
      const index = documentGroups.findIndex((g) => g.id === groupId);
      return DOCUMENT_COLORS[index % DOCUMENT_COLORS.length];
    },
    [documentGroups]
  );

  // Format page ranges for display
  const formatPageRanges = useCallback((pages: number[]): string => {
    if (pages.length === 0) return "Geen pagina's";

    const sorted = [...pages].sort((a, b) => a - b);
    const ranges: string[] = [];
    let start = sorted[0];
    let end = sorted[0];

    for (let i = 1; i < sorted.length; i++) {
      if (sorted[i] === end + 1) {
        end = sorted[i];
      } else {
        ranges.push(start === end ? `${start}` : `${start}-${end}`);
        start = sorted[i];
        end = sorted[i];
      }
    }
    ranges.push(start === end ? `${start}` : `${start}-${end}`);

    return ranges.join(", ");
  }, []);

  // Validate before split
  const validationErrors = useMemo(() => {
    const errors: string[] = [];

    // Check all pages are assigned
    const assignedPages = Object.keys(pageAssignments).length;
    if (assignedPages < numPages) {
      errors.push(`${numPages - assignedPages} pagina's nog niet toegewezen`);
    }

    // Check each group has pages
    const emptyGroups = documentGroups.filter((g) => g.pages.length === 0);
    if (emptyGroups.length > 0) {
      errors.push(`${emptyGroups.length} document(en) zonder pagina's`);
    }

    // Check for duplicate filenames
    const fileNames = documentGroups.map((g) => g.fileName.toLowerCase());
    const uniqueNames = new Set(fileNames);
    if (uniqueNames.size !== fileNames.length) {
      errors.push("Bestandsnamen moeten uniek zijn");
    }

    // Check filenames aren't empty
    if (documentGroups.some((g) => !g.fileName.replace(".pdf", "").trim())) {
      errors.push("Alle bestandsnamen moeten ingevuld zijn");
    }

    return errors;
  }, [pageAssignments, documentGroups, numPages]);

  // Perform the split
  const handleSplit = useCallback(async () => {
    if (validationErrors.length > 0 || !session) return;

    setIsSplitting(true);

    try {
      const splits = documentGroups
        .filter((g) => g.pages.length > 0)
        .map((g) => ({
          pageRanges: formatPageRanges(g.pages),
          newFileName: g.fileName,
        }));

      const response = await fetchWithAuth(
        `${API_URL}/building/${buildingId}/dropzone-split`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            fileId: file.id,
            fileName: file.name,
            folderId,
            splits,
            deleteOriginal,
          }),
        },
        session
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || "Splitsen mislukt");
      }

      const result = await response.json();
      toast.success(
        `PDF gesplitst in ${result.splits.length} documenten${
          result.originalDeleted ? " (origineel verwijderd)" : ""
        }`
      );

      // Pass the result with original file info for local state update
      onSplitComplete({
        splits: result.splits,
        originalDeleted: result.originalDeleted,
        originalFileId: file.id,
        folderPath: file.path,
      });
      handleClose();
    } catch (err) {
      console.error("Split error:", err);
      toast.error(err instanceof Error ? err.message : "Splitsen mislukt");
    } finally {
      setIsSplitting(false);
    }
  }, [
    validationErrors,
    documentGroups,
    buildingId,
    file,
    folderId,
    deleteOriginal,
    formatPageRanges,
    session,
    onSplitComplete,
  ]);

  // Reset state on close
  const handleClose = useCallback(() => {
    setPdfBlob(null);
    setNumPages(0);
    setPageAssignments({});
    setDocumentGroups([]);
    setDeleteOriginal(false);
    setError(null);
    onClose();
  }, [onClose]);

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && handleClose()}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Scissors weight="duotone" className="text-primary" size={24} />
            PDF Splitsen: {file.name}
          </DialogTitle>
          <DialogDescription>
            {numPages > 0
              ? `${numPages} pagina's - Klik op pagina's om ze aan een document toe te wijzen`
              : "PDF laden..."}
          </DialogDescription>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto space-y-4 py-4">
          {/* Loading state */}
          {isLoading && (
            <div className="flex items-center justify-center py-12">
              <SpinnerGap size={32} className="animate-spin text-primary" />
              <span className="ml-2 text-neutral-600">PDF laden...</span>
            </div>
          )}

          {/* Error state */}
          {error && (
            <div className="flex items-center justify-center py-12 text-red-600">
              <Warning size={24} className="mr-2" />
              {error}
            </div>
          )}

          {/* PDF Pages Grid */}
          {pdfBlob && !isLoading && !error && (
            <>
              <div className="bg-neutral-50 rounded-lg p-4">
                <Document
                  file={pdfBlob}
                  onLoadSuccess={({ numPages }) => setNumPages(numPages)}
                  loading={
                    <div className="flex items-center justify-center py-8">
                      <SpinnerGap size={24} className="animate-spin text-primary" />
                    </div>
                  }
                >
                  <div className="grid grid-cols-4 sm:grid-cols-5 md:grid-cols-6 lg:grid-cols-8 gap-3">
                    {Array.from({ length: numPages }, (_, i) => {
                      const pageNum = i + 1;
                      const assignedGroup = pageAssignments[pageNum];
                      const color = assignedGroup ? getColorForGroup(assignedGroup) : null;

                      return (
                        <div
                          key={i}
                          onClick={() => handlePageClick(pageNum)}
                          className={`cursor-pointer rounded-lg overflow-hidden border-2 transition-all hover:scale-105 hover:shadow-lg ${
                            color
                              ? `${color.border} ${color.bg}`
                              : "border-neutral-200 bg-white hover:border-neutral-400"
                          }`}
                        >
                          <div className="relative">
                            <Page
                              pageNumber={pageNum}
                              width={80}
                              renderTextLayer={false}
                              renderAnnotationLayer={false}
                            />
                            {/* Page number badge */}
                            <div
                              className={`absolute bottom-1 left-1/2 -translate-x-1/2 px-2 py-0.5 rounded text-xs font-medium ${
                                color ? `${color.bg} ${color.text}` : "bg-neutral-100 text-neutral-600"
                              }`}
                            >
                              {pageNum}
                            </div>
                            {/* Assigned indicator */}
                            {color && (
                              <div
                                className={`absolute top-1 right-1 w-3 h-3 rounded-full ${color.dot}`}
                              />
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </Document>
              </div>

              {/* Document Groups */}
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium text-neutral-700">Documenten</span>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={addDocumentGroup}
                    disabled={documentGroups.length >= DOCUMENT_COLORS.length}
                  >
                    <Plus size={16} className="mr-1" />
                    Document toevoegen
                  </Button>
                </div>

                {documentGroups.map((group) => {
                  const color = getColorForGroup(group.id);
                  return (
                    <div
                      key={group.id}
                      className={`p-3 rounded-lg border-2 ${color.border} ${color.bg}`}
                    >
                      <div className="flex items-center gap-3">
                        <div className={`w-4 h-4 rounded-full ${color.dot} flex-shrink-0`} />
                        <div className="flex-1 space-y-2">
                          <div className="flex items-center gap-2">
                            <Input
                              value={group.fileName.replace(/\.pdf$/i, "")}
                              onChange={(e) => updateFileName(group.id, e.target.value)}
                              className="h-8 text-sm bg-white"
                              placeholder="Bestandsnaam"
                            />
                            <span className="text-sm text-neutral-500">.pdf</span>
                            {documentGroups.length > 2 && (
                              <Button
                                type="button"
                                variant="ghost"
                                size="sm"
                                onClick={() => removeDocumentGroup(group.id)}
                                className="text-red-500 hover:text-red-700 hover:bg-red-50 h-8 w-8 p-0"
                              >
                                <Trash size={16} />
                              </Button>
                            )}
                          </div>
                          <div className={`text-xs ${color.text}`}>
                            Pagina's: {formatPageRanges(group.pages)} ({group.pages.length}{" "}
                            {group.pages.length === 1 ? "pagina" : "pagina's"})
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* Delete original checkbox */}
              <div className="flex items-center gap-2 pt-2">
                <Checkbox
                  id="delete-original"
                  checked={deleteOriginal}
                  onCheckedChange={(checked) => setDeleteOriginal(checked === true)}
                />
                <label
                  htmlFor="delete-original"
                  className="text-sm text-neutral-600 cursor-pointer"
                >
                  Origineel bestand verwijderen na splitsen
                </label>
              </div>

              {/* Validation errors */}
              {validationErrors.length > 0 && (
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
                  <div className="flex items-start gap-2">
                    <Warning size={20} className="text-amber-600 flex-shrink-0 mt-0.5" />
                    <div className="text-sm text-amber-800">
                      <span className="font-medium">Let op:</span>
                      <ul className="mt-1 list-disc list-inside">
                        {validationErrors.map((err, i) => (
                          <li key={i}>{err}</li>
                        ))}
                      </ul>
                    </div>
                  </div>
                </div>
              )}
            </>
          )}
        </div>

        <DialogFooter className="border-t pt-4">
          <Button type="button" variant="outline" onClick={handleClose} disabled={isSplitting}>
            Annuleren
          </Button>
          <Button
            type="button"
            onClick={handleSplit}
            disabled={isSplitting || validationErrors.length > 0 || numPages === 0}
          >
            {isSplitting ? (
              <>
                <SpinnerGap size={16} className="animate-spin mr-2" />
                Bezig met splitsen...
              </>
            ) : (
              <>
                <Check size={16} className="mr-2" />
                Splitsen
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
